import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCalendar, FiPlus, FiTrash2, FiX, FiCheck,
  FiAlertTriangle, FiSettings, FiDroplet, FiPhone, FiPackage, FiHash
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { settingsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useFinancialYear } from '../../context/FinancialYearContext';
import { STORAGE_KEY, defaultConfig } from '../Layout/FloatingActionButton';

const styles = {
  container: {
    fontFamily: 'Inter, sans-serif',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 28,
    flexWrap: 'wrap',
    gap: 16,
  },
  title: {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 28,
    fontWeight: 800,
    color: '#064e3b',
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 4,
  },
  addBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #059669, #10b981)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
    fontFamily: 'Poppins, sans-serif',
    whiteSpace: 'nowrap',
  },
  sectionCard: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    borderRadius: 20,
    border: '1px solid rgba(255, 255, 255, 0.5)',
    boxShadow: '0 8px 32px rgba(6, 78, 59, 0.08)',
    padding: 24,
    marginBottom: 24,
  },
  sectionTitle: {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 18,
    fontWeight: 700,
    color: '#064e3b',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 20,
  },
  sectionIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'linear-gradient(135deg, #059669, #10b981)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    flexShrink: 0,
  },
  fyCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderRadius: 14,
    border: '2px solid rgba(16, 185, 129, 0.15)',
    marginBottom: 10,
    transition: 'all 0.2s ease',
    flexWrap: 'wrap',
    gap: 12,
  },
  fyCardActive: {
    border: '2px solid #10b981',
    background: 'rgba(16, 185, 129, 0.06)',
  },
  fyLabel: {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 16,
    fontWeight: 700,
    color: '#064e3b',
  },
  fyDates: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 2,
  },
  activeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 12px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    background: '#dcfce7',
    color: '#166534',
  },
  actionBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    marginLeft: 6,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(6, 78, 59, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'white',
    borderRadius: 24,
    padding: '32px',
    width: '95%',
    maxWidth: 520,
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 25px 80px rgba(0, 0, 0, 0.2)',
    position: 'relative',
  },
  modalTitle: {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 22,
    fontWeight: 700,
    color: '#064e3b',
    marginBottom: 24,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  fieldLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#047857',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  fieldInput: {
    width: '100%',
    padding: '10px 14px',
    border: '2px solid rgba(16, 185, 129, 0.2)',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    transition: 'all 0.2s ease',
    background: '#f0fdf4',
    fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
  },
  saveBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 32px',
    background: 'linear-gradient(135deg, #059669, #10b981)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
    fontFamily: 'Poppins, sans-serif',
  },
  cancelBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    background: 'transparent',
    color: '#6b7280',
    border: '2px solid #e5e7eb',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'Poppins, sans-serif',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#9ca3af',
  },
};

function generateLabel(startDate) {
  if (!startDate) return '';
  const start = new Date(startDate);
  const startYear = start.getFullYear();
  const startMonth = start.getMonth();
  // Indian FY: if start is in April (month 3), label is "2025-26"
  if (startMonth >= 3) {
    return `${startYear}-${String(startYear + 1).slice(-2)}`;
  }
  return `${startYear - 1}-${String(startYear).slice(-2)}`;
}

function generateEndDate(startDate) {
  if (!startDate) return '';
  const start = new Date(startDate);
  const startYear = start.getFullYear();
  const startMonth = start.getMonth();
  if (startMonth >= 3) {
    // April start → ends March 31 next year
    return `${startYear + 1}-03-31`;
  }
  // Jan start → ends March 31 same year
  return `${startYear}-03-31`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const FORMAT_TOKENS = [
  { token: '{NNN}', desc: 'Serial number, 3 digits (001)' },
  { token: '{N}', desc: 'Serial number, no padding (1)' },
  { token: '{PREFIX}', desc: 'Prefix / code (company initials if blank)' },
  { token: '{FY}', desc: 'Financial year (26-27)' },
  { token: '{YYYY}', desc: 'Current year (2026)' },
  { token: '{YY}', desc: 'Current year, 2 digits (26)' },
];

function renderNumberPreview(format, prefix, fyLabel, num) {
  const now = new Date();
  return (format || '')
    .split('{PREFIX}').join(prefix || '')
    .split('{NNN}').join(String(num).padStart(3, '0'))
    .split('{N}').join(String(num))
    .split('{FY}').join(fyLabel || '')
    .split('{YYYY}').join(String(now.getFullYear()))
    .split('{YY}').join(String(now.getFullYear() % 100).padStart(2, '0'));
}

function loadFloatingConfig() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...defaultConfig, ...JSON.parse(stored) };
  } catch {}
  return { ...defaultConfig };
}

export default function Settings() {
  useAuth();
  const { refreshFinancialYear } = useFinancialYear();

  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ start_date: '', end_date: '', label: '' });

  // Package types
  const [packageTypes, setPackageTypes] = useState([]);
  const [newPkgType, setNewPkgType] = useState('');
  const [pkgLoading, setPkgLoading] = useState(true);
  const [deletePkgModal, setDeletePkgModal] = useState(null);

  // Invoice numbering
  const [numbering, setNumbering] = useState(null);
  const [numForm, setNumForm] = useState({
    invoice_prefix: '',
    invoice_starting_number: 1,
    invoice_number_format: '',
    proforma_invoice_prefix: '',
    proforma_invoice_starting_number: 1,
    proforma_number_format: '',
  });
  const [numLoading, setNumLoading] = useState(true);
  const [numSaving, setNumSaving] = useState(false);

  // Floating button config
  const [floatingConfig, setFloatingConfig] = useState(loadFloatingConfig);

  const fetchFinancialYears = useCallback(async () => {
    setLoading(true);
    try {
      const res = await settingsAPI.getFinancialYears();
      setFinancialYears(res.data || []);
    } catch {
      toast.error('Failed to load financial years');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPackageTypes = useCallback(async () => {
    setPkgLoading(true);
    try {
      const res = await settingsAPI.getPackageTypes();
      setPackageTypes(res.data || []);
    } catch {
      toast.error('Failed to load package types');
    } finally {
      setPkgLoading(false);
    }
  }, []);

  const fetchNumbering = useCallback(async () => {
    setNumLoading(true);
    try {
      const res = await settingsAPI.getNumbering();
      setNumbering(res.data);
      setNumForm({
        invoice_prefix: res.data.invoice_prefix || '',
        invoice_starting_number: res.data.invoice_starting_number || 1,
        invoice_number_format: res.data.invoice_number_format || '',
        proforma_invoice_prefix: res.data.proforma_invoice_prefix || '',
        proforma_invoice_starting_number: res.data.proforma_invoice_starting_number || 1,
        proforma_number_format: res.data.proforma_number_format || '',
      });
    } catch {
      toast.error('Failed to load invoice numbering settings');
    } finally {
      setNumLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFinancialYears();
    fetchPackageTypes();
    fetchNumbering();
  }, [fetchFinancialYears, fetchPackageTypes, fetchNumbering]);

  const handleNumFieldChange = (key, value) => {
    setNumForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveNumbering = async () => {
    const invFormat = numForm.invoice_number_format.trim();
    const piFormat = numForm.proforma_number_format.trim();
    const hasSerial = (f) => f.includes('{NNN}') || f.includes('{N}');
    if (invFormat && !hasSerial(invFormat)) {
      toast.error('Commercial invoice format must include {NNN} or {N}');
      return;
    }
    if (piFormat && !hasSerial(piFormat)) {
      toast.error('Proforma invoice format must include {NNN} or {N}');
      return;
    }
    setNumSaving(true);
    try {
      const res = await settingsAPI.updateNumbering({
        invoice_prefix: numForm.invoice_prefix.trim(),
        invoice_starting_number: Math.max(1, Number(numForm.invoice_starting_number) || 1),
        invoice_number_format: invFormat,
        proforma_invoice_prefix: numForm.proforma_invoice_prefix.trim(),
        proforma_invoice_starting_number: Math.max(1, Number(numForm.proforma_invoice_starting_number) || 1),
        proforma_number_format: piFormat,
      });
      setNumbering(res.data);
      toast.success('Invoice numbering settings saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save numbering settings');
    } finally {
      setNumSaving(false);
    }
  };

  const handleAddPkgType = async () => {
    const name = newPkgType.trim();
    if (!name) {
      toast.error('Package type name is required');
      return;
    }
    try {
      await settingsAPI.createPackageType({ name });
      toast.success('Package type added');
      setNewPkgType('');
      fetchPackageTypes();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add package type');
    }
  };

  const handleDeletePkgType = async () => {
    if (!deletePkgModal) return;
    try {
      await settingsAPI.deletePackageType(deletePkgModal.id);
      toast.success('Package type deleted');
      setDeletePkgModal(null);
      fetchPackageTypes();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  const openCreate = () => {
    // Default to current Indian FY start
    const now = new Date();
    const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const startDate = `${fyStartYear}-04-01`;
    const endDate = `${fyStartYear + 1}-03-31`;
    const label = `${fyStartYear}-${String(fyStartYear + 1).slice(-2)}`;
    setForm({ start_date: startDate, end_date: endDate, label });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm({ start_date: '', end_date: '', label: '' });
  };

  const handleStartDateChange = (val) => {
    const endDate = generateEndDate(val);
    const label = generateLabel(val);
    setForm({ start_date: val, end_date: endDate, label });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date || !form.label) {
      toast.error('All fields are required');
      return;
    }
    setSaving(true);
    try {
      await settingsAPI.createFinancialYear(form);
      toast.success('Financial year created');
      closeModal();
      fetchFinancialYears();
      refreshFinancialYear();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create financial year');
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (id) => {
    try {
      await settingsAPI.setActiveFinancialYear(id);
      toast.success('Financial year activated');
      fetchFinancialYears();
      refreshFinancialYear();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to activate');
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      await settingsAPI.deleteFinancialYear(deleteModal.id);
      toast.success('Financial year deleted');
      setDeleteModal(null);
      fetchFinancialYears();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleFloatingConfigChange = (key, value) => {
    setFloatingConfig(prev => ({ ...prev, [key]: value }));
  };

  const saveFloatingConfig = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(floatingConfig));
    toast.success('Floating button settings saved');
  };

  const fyLabel = numbering?.fy_label || '';
  const commercialPreview = renderNumberPreview(
    numForm.invoice_number_format.trim() || numbering?.default_invoice_format || '{PREFIX}{NNN}',
    numForm.invoice_prefix.trim() || numbering?.invoice_effective_prefix || '',
    fyLabel,
    Math.max(1, Number(numForm.invoice_starting_number) || 1)
  );
  // Mirror backend proformaCode: strip a leading "PI" marker — the "PI-"
  // literal belongs in the number format, {PREFIX} is the bare company code.
  const cleanedPiPrefix = numForm.proforma_invoice_prefix
    .trim()
    .replace(/^[-/\s]+|[-/\s]+$/g, '')
    .replace(/^PI([-/\s]+|$)/i, '')
    .replace(/^[-/\s]+/, '');
  const proformaPreview = renderNumberPreview(
    numForm.proforma_number_format.trim() || numbering?.default_proforma_format || '{NNN}/PI-{PREFIX}/{FY}',
    cleanedPiPrefix || numbering?.proforma_effective_prefix || '',
    fyLabel,
    Math.max(1, Number(numForm.proforma_invoice_starting_number) || 1)
  );

  return (
    <div style={styles.container}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={styles.header}
      >
        <div>
          <h1 style={styles.title}>Settings</h1>
          <p style={styles.subtitle}>Manage application settings and financial years</p>
        </div>
      </motion.div>

      {/* Financial Year Section */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1 }}
        style={styles.sectionCard}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <h2 style={styles.sectionTitle}>
            <div style={styles.sectionIcon}>
              <FiCalendar size={18} />
            </div>
            Financial Years
          </h2>
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            style={styles.addBtn}
            onClick={openCreate}
          >
            <FiPlus size={18} />
            Add Financial Year
          </motion.button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{
              width: 40, height: 40,
              border: '3px solid rgba(16, 185, 129, 0.2)',
              borderTopColor: '#10b981',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: '#6b7280', fontSize: 14 }}>Loading...</p>
          </div>
        ) : financialYears.length === 0 ? (
          <div style={styles.emptyState}>
            <FiCalendar size={48} style={{ marginBottom: 16, opacity: 0.4 }} />
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>No financial years configured</p>
            <p style={{ fontSize: 13 }}>Click "Add Financial Year" to create your first one</p>
          </div>
        ) : (
          financialYears.map((fy, idx) => (
            <motion.div
              key={fy.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              style={{
                ...styles.fyCard,
                ...(fy.is_active ? styles.fyCardActive : {}),
              }}
            >
              <div>
                <div style={styles.fyLabel}>
                  FY {fy.label}
                  {fy.is_active && (
                    <span style={{ ...styles.activeBadge, marginLeft: 10 }}>
                      <FiCheck size={12} /> Active
                    </span>
                  )}
                </div>
                <div style={styles.fyDates}>
                  {formatDate(fy.start_date)} — {formatDate(fy.end_date)}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {!fy.is_active && (
                  <motion.button
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.95 }}
                    title="Set as Active"
                    onClick={() => handleSetActive(fy.id)}
                    style={{
                      ...styles.actionBtn,
                      background: 'rgba(16, 185, 129, 0.1)',
                      color: '#059669',
                    }}
                  >
                    <FiCheck size={16} />
                  </motion.button>
                )}
                {!fy.is_active && (
                  <motion.button
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.95 }}
                    title="Delete"
                    onClick={() => setDeleteModal(fy)}
                    style={{
                      ...styles.actionBtn,
                      background: 'rgba(239, 68, 68, 0.1)',
                      color: '#dc2626',
                    }}
                  >
                    <FiTrash2 size={15} />
                  </motion.button>
                )}
              </div>
            </motion.div>
          ))
        )}
      </motion.div>

      {/* Package Types Section */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.15 }}
        style={styles.sectionCard}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <h2 style={styles.sectionTitle}>
            <div style={styles.sectionIcon}>
              <FiPackage size={18} />
            </div>
            Package Types
          </h2>
        </div>

        {/* Inline add form */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <input
            type="text"
            value={newPkgType}
            onChange={(e) => setNewPkgType(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddPkgType()}
            placeholder="Enter new package type..."
            style={{ ...styles.fieldInput, flex: 1 }}
          />
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            style={styles.addBtn}
            onClick={handleAddPkgType}
          >
            <FiPlus size={16} />
            Add
          </motion.button>
        </div>

        {pkgLoading ? (
          <div style={{ textAlign: 'center', padding: 30 }}>
            <div style={{
              width: 36, height: 36,
              border: '3px solid rgba(16, 185, 129, 0.2)',
              borderTopColor: '#10b981',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 12px',
            }} />
            <p style={{ color: '#6b7280', fontSize: 13 }}>Loading...</p>
          </div>
        ) : packageTypes.length === 0 ? (
          <div style={styles.emptyState}>
            <FiPackage size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>No package types configured</p>
            <p style={{ fontSize: 13 }}>Add your first package type above</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {packageTypes.map((pt) => (
              <motion.div
                key={pt.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 14px',
                  borderRadius: 10,
                  border: '1.5px solid rgba(16, 185, 129, 0.2)',
                  background: 'rgba(16, 185, 129, 0.04)',
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#064e3b',
                }}
              >
                <span>{pt.name}</span>
                <button
                  onClick={() => setDeletePkgModal(pt)}
                  title="Delete"
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    border: 'none',
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#dc2626',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                >
                  <FiX size={12} />
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Invoice Numbering Section */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.18 }}
        style={styles.sectionCard}
      >
        <h2 style={styles.sectionTitle}>
          <div style={styles.sectionIcon}>
            <FiHash size={18} />
          </div>
          Invoice Numbering
        </h2>

        {numLoading ? (
          <div style={{ textAlign: 'center', padding: 30 }}>
            <div style={{
              width: 36, height: 36,
              border: '3px solid rgba(16, 185, 129, 0.2)',
              borderTopColor: '#10b981',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 12px',
            }} />
            <p style={{ color: '#6b7280', fontSize: 13 }}>Loading...</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginBottom: 20 }}>
              {/* Proforma Invoice */}
              <div style={{
                padding: 18, borderRadius: 14,
                border: '1.5px solid rgba(16, 185, 129, 0.2)',
                background: 'rgba(16, 185, 129, 0.03)',
              }}>
                <h3 style={{
                  fontFamily: 'Poppins, sans-serif', fontSize: 15, fontWeight: 700,
                  color: '#064e3b', margin: '0 0 14px',
                }}>
                  Proforma Invoice Number
                </h3>
                <div style={{ marginBottom: 12 }}>
                  <label style={styles.fieldLabel}>Number Format</label>
                  <input
                    type="text"
                    value={numForm.proforma_number_format}
                    onChange={(e) => handleNumFieldChange('proforma_number_format', e.target.value)}
                    placeholder={numbering?.default_proforma_format || '{NNN}/PI-{PREFIX}/{FY}'}
                    style={styles.fieldInput}
                  />
                  <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                    Leave blank for default: {numbering?.default_proforma_format || '{NNN}/PI-{PREFIX}/{FY}'}
                  </p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={styles.fieldLabel}>Prefix / Code</label>
                    <input
                      type="text"
                      value={numForm.proforma_invoice_prefix}
                      onChange={(e) => handleNumFieldChange('proforma_invoice_prefix', e.target.value)}
                      placeholder="e.g. KCIPL (blank = company initials)"
                      style={styles.fieldInput}
                    />
                  </div>
                  <div>
                    <label style={styles.fieldLabel}>Starting Number</label>
                    <input
                      type="number"
                      min="1"
                      value={numForm.proforma_invoice_starting_number}
                      onChange={(e) => handleNumFieldChange('proforma_invoice_starting_number', e.target.value)}
                      style={styles.fieldInput}
                    />
                  </div>
                </div>
                <div style={{
                  padding: '10px 14px', borderRadius: 10,
                  background: 'rgba(16, 185, 129, 0.08)',
                  border: '1px dashed rgba(16, 185, 129, 0.35)',
                  fontSize: 13, color: '#047857',
                }}>
                  Preview: <strong style={{ fontFamily: 'monospace', fontSize: 14 }}>{proformaPreview}</strong>
                </div>
              </div>

              {/* Commercial Invoice */}
              <div style={{
                padding: 18, borderRadius: 14,
                border: '1.5px solid rgba(16, 185, 129, 0.2)',
                background: 'rgba(16, 185, 129, 0.03)',
              }}>
                <h3 style={{
                  fontFamily: 'Poppins, sans-serif', fontSize: 15, fontWeight: 700,
                  color: '#064e3b', margin: '0 0 14px',
                }}>
                  Commercial Invoice Number
                </h3>
                <div style={{ marginBottom: 12 }}>
                  <label style={styles.fieldLabel}>Number Format</label>
                  <input
                    type="text"
                    value={numForm.invoice_number_format}
                    onChange={(e) => handleNumFieldChange('invoice_number_format', e.target.value)}
                    placeholder={numbering?.default_invoice_format || '{PREFIX}{NNN}'}
                    style={styles.fieldInput}
                  />
                  <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                    Leave blank for default: {numbering?.default_invoice_format || '{PREFIX}{NNN}'}
                  </p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={styles.fieldLabel}>Prefix</label>
                    <input
                      type="text"
                      value={numForm.invoice_prefix}
                      onChange={(e) => handleNumFieldChange('invoice_prefix', e.target.value)}
                      placeholder="e.g. INV- (blank = company initials)"
                      style={styles.fieldInput}
                    />
                  </div>
                  <div>
                    <label style={styles.fieldLabel}>Starting Number</label>
                    <input
                      type="number"
                      min="1"
                      value={numForm.invoice_starting_number}
                      onChange={(e) => handleNumFieldChange('invoice_starting_number', e.target.value)}
                      style={styles.fieldInput}
                    />
                  </div>
                </div>
                <div style={{
                  padding: '10px 14px', borderRadius: 10,
                  background: 'rgba(16, 185, 129, 0.08)',
                  border: '1px dashed rgba(16, 185, 129, 0.35)',
                  fontSize: 13, color: '#047857',
                }}>
                  Preview: <strong style={{ fontFamily: 'monospace', fontSize: 14 }}>{commercialPreview}</strong>
                </div>
              </div>
            </div>

            {/* Token legend */}
            <div style={{
              padding: '12px 16px', borderRadius: 10,
              background: 'rgba(16, 185, 129, 0.06)',
              border: '1px solid rgba(16, 185, 129, 0.15)',
              marginBottom: 20,
            }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#047857', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Available Tokens
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {FORMAT_TOKENS.map((t) => (
                  <span key={t.token} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '4px 10px', borderRadius: 8,
                    background: 'white', border: '1px solid rgba(16, 185, 129, 0.25)',
                    fontSize: 12, color: '#064e3b',
                  }}>
                    <code style={{ fontWeight: 700, color: '#059669' }}>{t.token}</code>
                    {t.desc}
                  </span>
                ))}
              </div>
              <p style={{ fontSize: 12, color: '#6b7280', marginTop: 10, marginBottom: 0 }}>
                Numbering restarts every financial year when the format includes {'{FY}'}. Existing invoice numbers are not changed.
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleSaveNumbering}
                disabled={numSaving}
                style={{ ...styles.saveBtn, opacity: numSaving ? 0.7 : 1, cursor: numSaving ? 'not-allowed' : 'pointer' }}
              >
                <FiCheck size={16} />
                {numSaving ? 'Saving...' : 'Save Numbering Settings'}
              </motion.button>
            </div>
          </>
        )}
      </motion.div>

      {/* Theme & Colors — Floating Button */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.2 }}
        style={styles.sectionCard}
      >
        <h2 style={styles.sectionTitle}>
          <div style={styles.sectionIcon}>
            <FiDroplet size={18} />
          </div>
          Theme & Colors — Floating Button
        </h2>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#1f2937' }}>
            <input
              type="checkbox"
              checked={floatingConfig.enabled}
              onChange={(e) => handleFloatingConfigChange('enabled', e.target.checked)}
              style={{ width: 18, height: 18, accentColor: '#10b981', cursor: 'pointer' }}
            />
            Enable Floating Button
          </label>
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4, marginLeft: 28 }}>
            Show a floating action button on all pages with WhatsApp and Phone Call shortcuts
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          <div>
            <label style={styles.fieldLabel}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#25d366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp Number
              </span>
            </label>
            <input
              type="text"
              value={floatingConfig.whatsapp_number}
              onChange={(e) => handleFloatingConfigChange('whatsapp_number', e.target.value)}
              placeholder="e.g. 919876543210 (with country code)"
              style={styles.fieldInput}
            />
            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Include country code without + sign</p>
          </div>

          <div>
            <label style={styles.fieldLabel}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FiPhone size={14} color="#3b82f6" />
                Phone Number
              </span>
            </label>
            <input
              type="text"
              value={floatingConfig.phone_number}
              onChange={(e) => handleFloatingConfigChange('phone_number', e.target.value)}
              placeholder="e.g. +919876543210"
              style={styles.fieldInput}
            />
            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Include country code with + sign</p>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={saveFloatingConfig}
            style={styles.saveBtn}
          >
            <FiCheck size={16} />
            Save Floating Button Settings
          </motion.button>
        </div>
      </motion.div>

      {/* Create Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.overlay}
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.93, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 20 }}
              transition={{ duration: 0.25 }}
              style={styles.modal}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                style={{
                  position: 'absolute', top: 16, right: 16,
                  background: 'rgba(239, 68, 68, 0.08)', border: 'none',
                  borderRadius: 8, width: 36, height: 36,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: '#dc2626',
                }}
              >
                <FiX size={18} />
              </button>

              <div style={styles.modalTitle}>
                <div style={{
                  width: 40, height: 40, borderRadius: 12,
                  background: 'linear-gradient(135deg, #059669, #10b981)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <FiPlus size={18} color="white" />
                </div>
                Add Financial Year
              </div>

              <form onSubmit={handleSave}>
                <div style={{ marginBottom: 16 }}>
                  <label style={styles.fieldLabel}>Label *</label>
                  <input
                    type="text"
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    style={styles.fieldInput}
                    placeholder="e.g. 2025-26"
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={styles.fieldLabel}>Start Date *</label>
                    <input
                      type="date"
                      value={form.start_date}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                      style={styles.fieldInput}
                      required
                    />
                  </div>
                  <div>
                    <label style={styles.fieldLabel}>End Date *</label>
                    <input
                      type="date"
                      value={form.end_date}
                      onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                      style={styles.fieldInput}
                      required
                    />
                  </div>
                </div>

                <div style={{
                  padding: '12px 16px', borderRadius: 10,
                  background: 'rgba(16, 185, 129, 0.06)',
                  border: '1px solid rgba(16, 185, 129, 0.15)',
                  marginBottom: 24, fontSize: 13, color: '#047857',
                }}>
                  <FiSettings size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                  Indian Financial Year runs April 1 to March 31. Changing the start date will auto-fill the end date and label.
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                  <button type="button" style={styles.cancelBtn} onClick={closeModal}>Cancel</button>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    type="submit"
                    style={{ ...styles.saveBtn, opacity: saving ? 0.7 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
                    disabled={saving}
                  >
                    <FiCalendar size={16} />
                    {saving ? 'Creating...' : 'Create Financial Year'}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.overlay}
            onClick={() => setDeleteModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              style={{ ...styles.modal, maxWidth: 440, textAlign: 'center', padding: '40px 32px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                width: 60, height: 60, borderRadius: 16,
                background: 'rgba(239, 68, 68, 0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <FiAlertTriangle size={28} color="#dc2626" />
              </div>
              <h3 style={{
                fontFamily: 'Poppins, sans-serif', fontSize: 20,
                fontWeight: 700, color: '#1f2937', marginBottom: 8,
              }}>
                Delete Financial Year?
              </h3>
              <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 28 }}>
                Are you sure you want to delete{' '}
                <strong style={{ color: '#064e3b' }}>FY {deleteModal.label}</strong>?
                This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button style={styles.cancelBtn} onClick={() => setDeleteModal(null)}>Cancel</button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  style={{
                    ...styles.saveBtn,
                    background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                    boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
                  }}
                  onClick={handleDelete}
                >
                  <FiTrash2 size={16} />
                  Delete
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Delete Package Type Confirmation Modal */}
      <AnimatePresence>
        {deletePkgModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.overlay}
            onClick={() => setDeletePkgModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              style={{ ...styles.modal, maxWidth: 440, textAlign: 'center', padding: '40px 32px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                width: 60, height: 60, borderRadius: 16,
                background: 'rgba(239, 68, 68, 0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <FiAlertTriangle size={28} color="#dc2626" />
              </div>
              <h3 style={{
                fontFamily: 'Poppins, sans-serif', fontSize: 20,
                fontWeight: 700, color: '#1f2937', marginBottom: 8,
              }}>
                Delete Package Type?
              </h3>
              <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 28 }}>
                Are you sure you want to delete{' '}
                <strong style={{ color: '#064e3b' }}>{deletePkgModal.name}</strong>?
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button style={styles.cancelBtn} onClick={() => setDeletePkgModal(null)}>Cancel</button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  style={{
                    ...styles.saveBtn,
                    background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                    boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
                  }}
                  onClick={handleDeletePkgType}
                >
                  <FiTrash2 size={16} />
                  Delete
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
